import {
  Avatar,
  AvatarColorOption,
  CollapseCard,
  FlexBox,
  IconNames,
  Text,
} from '@dixa/lib-shared-habla';
import { PropsWithChildren, ReactNode, useState } from 'react';

import { UserAuditLogEntry } from 'apis/auditLogs';
import { ConversationTimestamp } from 'components/conversation/components/conversationTimestamp/conversationTimestamp';

import { UserLink } from '../activityTypes/userLink/userLink';
import { DetailField, hasValue } from './detailField';
import styles from './userActivityCard.module.scss';

export type CardField = DetailField;

type UserActivityCardProps = {
  readonly entry: UserAuditLogEntry;
  /** The avatar icon for the event. */
  readonly icon: IconNames;
  /** The tinted-circle color for the avatar (see `statusColors`). */
  readonly color: AvatarColorOption;
  /** The card title, e.g. "Order created". */
  readonly title: string;
  /** Rendered inline right after the title, e.g. a linked conversation id. */
  readonly titleSuffix?: ReactNode;
  /** An at-a-glance summary shown under the title, visible without expanding. */
  readonly summary?: ReactNode;
  /** Labelled detail columns revealed when the card is expanded. */
  readonly fields?: readonly CardField[];
};

/**
 * A single Customer Activities entry, rendered as an expandable habla
 * `CollapseCard`: a tinted-circle avatar, title, timestamp and an at-a-glance
 * summary in the header, with the labelled detail columns and any line items
 * revealed on expand.
 */
export const UserActivityCard = ({
  entry,
  icon,
  color,
  title,
  titleSuffix,
  summary,
  fields = [],
  children,
}: PropsWithChildren<UserActivityCardProps>): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const shownFields = fields.filter(hasValue);
  const expandable = shownFields.length > 0 || children != null;

  return (
    <CollapseCard
      className={styles.card}
      isOpen={expandable && isOpen}
      onToggle={expandable ? setIsOpen : () => undefined}
    >
      <CollapseCard.Header
        className={styles.headerRow}
        avatar={
          <span className={styles.avatar}>
            <Avatar icon={icon} color={color} size="24" />
          </span>
        }
        title={title}
        wrap="normal"
        compact
        nonCollapsible={!expandable}
        slotAfterTitle={
          <ConversationTimestamp
            shouldDateFormatPersist
            time={entry.event_timestamp}
            isSecondsDisplayed={false}
          />
        }
      >
        {title}
        {titleSuffix != null && (
          <span className={styles.inlineMeta}>{titleSuffix}</span>
        )}
        {entry.author.name && entry.author.id !== entry.resource.id && (
          <Text.Paragraph
            as="span"
            variant="4"
            subdued
            className={styles.inlineMeta}
          >
            by{' '}
            <UserLink
              userId={entry.author.id}
              onClick={(event) => event.stopPropagation()}
            >
              {entry.author.name}
            </UserLink>
          </Text.Paragraph>
        )}
        {summary && (
          <Text.Paragraph
            as="span"
            variant="4"
            subdued
            className={styles.summary}
          >
            {summary}
          </Text.Paragraph>
        )}
      </CollapseCard.Header>
      {expandable && (
        <CollapseCard.Content>
          <FlexBox
            as="li"
            flexDirection="column"
            gap="300"
            padding="300"
            margin="0 0 300 500"
            background="neutral.100"
            borderRadius="100"
            className={styles.panel}
          >
            {shownFields.length > 0 && (
              <div className={styles.fields}>
                {shownFields.map((field, index) => (
                  <DetailField key={index} {...field} />
                ))}
              </div>
            )}
            {children}
          </FlexBox>
        </CollapseCard.Content>
      )}
    </CollapseCard>
  );
};
